import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  CalendarDays, Plus, ChevronLeft, ChevronRight, Clock, X, Check,
  Waves, RefreshCw, Ban, Trash2, MessageCircle, CheckCircle2, Handshake
} from 'lucide-react';
import { AddToCalendarButton } from '@/components/AddToCalendar';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const STATUS_STYLES = {
  scheduled: 'bg-cyan-100 text-cyan-800 border-cyan-200',
  completed: 'bg-green-100 text-green-800 border-green-200',
  cancelled: 'bg-red-100 text-red-800 border-red-200',
  rescheduled: 'bg-amber-100 text-amber-800 border-amber-200',
};

export default function SchedulePage() {
  const { user } = useAuth();
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showCancel, setShowCancel] = useState(null);
  const [showReschedule, setShowReschedule] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState({ startHour: '', startMinute: '00', startPeriod: 'AM', endHour: '', endMinute: '00', endPeriod: 'AM' });
  const [clickedDate, setClickedDate] = useState(null);
  const [quickAddTime, setQuickAddTime] = useState({ startHour: '', startMinute: '00', startPeriod: 'AM', endHour: '', endMinute: '00', endPeriod: 'AM' });
  const [quickAdding, setQuickAdding] = useState(false);
  const [dragEntry, setDragEntry] = useState(null);
  const [dragOverDate, setDragOverDate] = useState(null);
  // Parent meeting booking
  const [showBookMeeting, setShowBookMeeting] = useState(false);
  const [meetingType, setMeetingType] = useState('meet_and_greet');
  const [meetingDate, setMeetingDate] = useState('');
  const [meetingTime, setMeetingTime] = useState('');
  const [bookingMeeting, setBookingMeeting] = useState(false);
  const [pendingMeetings, setPendingMeetings] = useState([]);

  // Create form state
  const [selectedDates, setSelectedDates] = useState([]);
  const [recurringDays, setRecurringDays] = useState([]);
  const [recurringUntil, setRecurringUntil] = useState('');
  const [startHour, setStartHour] = useState('');
  const [startMinute, setStartMinute] = useState('00');
  const [startPeriod, setStartPeriod] = useState('AM');
  const [endHour, setEndHour] = useState('');
  const [endMinute, setEndMinute] = useState('00');
  const [endPeriod, setEndPeriod] = useState('AM');
  const [notes, setNotes] = useState('');
  const [createMode, setCreateMode] = useState('dates'); // 'dates' or 'recurring'
  const [scheduleType, setScheduleType] = useState('session'); // 'session' or 'meet_and_greet'
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [creating, setCreating] = useState(false);

  const HOURS = ['01','02','03','04','05','06','07','08','09','10','11','12'];
  const MINUTES = ['00','15','30','45'];

  const buildTimeSlot = () => {
    if (!startHour || !endHour) return '';
    return `${startHour}:${startMinute} ${startPeriod} - ${endHour}:${endMinute} ${endPeriod}`;
  };

  const isInstructor = user?.role === 'instructor';

  const fetchChildren = useCallback(async () => {
    try {
      const res = await api.get('/children');
      setChildren(res.data);
      if (res.data.length === 1) setSelectedChild(res.data[0]);
    } catch (e) { process.env.NODE_ENV === 'development' && console.error(e); } finally { setLoading(false); }
  }, []);

  const fetchSchedules = useCallback(async () => {
    if (!selectedChild) return;
    try {
      const res = await api.get(`/schedules?child_id=${selectedChild.child_id}`);
      setSchedules(res.data);
    } catch (e) { process.env.NODE_ENV === 'development' && console.error(e); }
  }, [selectedChild]);

  useEffect(() => {
    fetchChildren();
  }, [fetchChildren]);

  useEffect(() => {
    if (selectedChild) fetchSchedules();
  }, [selectedChild, fetchSchedules]);

  // Fetch pending meetings for this child (for parents to see meeting requests)
  useEffect(() => {
    if (!selectedChild) return;
    api.get(`/meet-greet/all/${selectedChild.child_id}`)
      .then(r => setPendingMeetings(r.data.filter(m => m.status !== 'cancelled')))
      .catch(() => {});
  }, [selectedChild, schedules]);

  const bookMeeting = async () => {
    if (!meetingDate || !selectedChild) { toast.error('Please select a date'); return; }
    setBookingMeeting(true);
    try {
      await api.post('/meet-greet/request', {
        child_id: selectedChild.child_id,
        proposed_date: meetingDate,
        proposed_time: meetingTime,
        meeting_type: meetingType,
      });
      const labels = { meet_and_greet: 'Meet & Greet', re_evaluation: 'Re-evaluation', other: 'Meeting' };
      toast.success(`${labels[meetingType] || 'Meeting'} request sent!`);
      setShowBookMeeting(false);
      setMeetingDate('');
      setMeetingTime('');
      fetchSchedules();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to book meeting'); }
    finally { setBookingMeeting(false); }
  };

  const approveMeeting = async (mgId) => {
    try {
      await api.put(`/meet-greet/${mgId}/respond`, { action: 'approve' });
      toast.success('Meeting confirmed!');
      fetchSchedules();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to approve'); }
  };

  const createSchedule = async () => {
    if (!selectedChild) return;
    const timeSlot = buildTimeSlot();
    if (!timeSlot) {
      toast.error('Time slot is required');
      return;
    }
    setCreating(true);
    try {
      const payload = {
        child_id: selectedChild.child_id,
        time_slot: timeSlot,
        notes,
        schedule_type: scheduleType,
      };
      if (createMode === 'recurring' && recurringDays.length > 0) {
        payload.recurring = { days: recurringDays, until: recurringUntil || undefined };
      }
      if (selectedDates.length > 0) {
        payload.dates = selectedDates;
      }
      if ((createMode === 'dates' && selectedDates.length === 0) || (createMode === 'recurring' && recurringDays.length === 0)) {
        toast.error('Select at least one date or recurring day');
        setCreating(false);
        return;
      }
      await api.post('/schedules', payload);
      toast.success('Schedule created!');
      setShowCreate(false);
      resetForm();
      fetchSchedules();
    } catch { toast.error('Failed to create schedule'); }
    finally { setCreating(false); }
  };

  const resetForm = () => {
    setSelectedDates([]);
    setRecurringDays([]);
    setRecurringUntil('');
    setStartHour('');
    setStartMinute('00');
    setStartPeriod('AM');
    setEndHour('');
    setEndMinute('00');
    setEndPeriod('AM');
    setNotes('');
    setCreateMode('dates');
    setScheduleType('session');
  };

  const handleCancelEntry = async () => {
    if (!showCancel) return;
    try {
      await api.put(`/schedules/${showCancel.schedule_id}/entries/${showCancel.entry_id}/cancel`, { reason: cancelReason });
      toast.success('Session cancelled');
      setShowCancel(null);
      setCancelReason('');
      fetchSchedules();
    } catch { toast.error('Failed to cancel'); }
  };

  const handleRescheduleEntry = async () => {
    if (!showReschedule || !rescheduleDate) { toast.error('Select a new date'); return; }
    const { startHour: rsh, startMinute: rsm, startPeriod: rsp, endHour: reh, endMinute: rem, endPeriod: rep } = rescheduleTime;
    const newTime = (rsh && reh) ? `${rsh}:${rsm} ${rsp} - ${reh}:${rem} ${rep}` : '';
    try {
      await api.put(`/schedules/${showReschedule.schedule_id}/entries/${showReschedule.entry_id}/reschedule`, {
        proposed_date: rescheduleDate,
        reason: rescheduleReason,
        new_time: newTime,
      });
      toast.success('Reschedule requested!');
      setShowReschedule(null);
      setRescheduleDate('');
      setRescheduleReason('');
      setRescheduleTime({ startHour: '', startMinute: '00', startPeriod: 'AM', endHour: '', endMinute: '00', endPeriod: 'AM' });
      fetchSchedules();
    } catch { toast.error('Failed to request reschedule'); }
  };

  const handleRespondReschedule = async (scheduleId, entryId, accepted) => {
    try {
      await api.put(`/schedules/${scheduleId}/entries/${entryId}/respond-reschedule`, { accepted });
      toast.success(accepted ? 'Reschedule accepted!' : 'Reschedule declined');
      fetchSchedules();
    } catch { toast.error('Failed to respond'); }
  };

  const deleteSchedule = async (scheduleId) => {
    if (!window.confirm('Delete this entire schedule?')) return;
    try {
      await api.delete(`/schedules/${scheduleId}`);
      toast.success('Schedule deleted');
      fetchSchedules();
    } catch { toast.error('Failed to delete'); }
  };

  const deleteEntry = async (scheduleId, entryId) => {
    try {
      await api.delete(`/schedules/${scheduleId}/entries/${entryId}`);
      toast.success('Session removed');
      setClickedDate(null);
      fetchSchedules();
    } catch { toast.error('Failed to delete session'); }
  };

  const quickAddSession = async () => {
    if (!clickedDate || !selectedChild) return;
    const { startHour: sh, startMinute: sm, startPeriod: sp, endHour: eh, endMinute: em, endPeriod: ep } = quickAddTime;
    if (!sh || !eh) { toast.error('Select a time slot'); return; }
    const timeSlot = `${sh}:${sm} ${sp} - ${eh}:${em} ${ep}`;
    setQuickAdding(true);
    try {
      // Find an existing active schedule to add to, or create a new one
      const activeSchedule = schedules.find(s => s.schedule_type === 'session');
      if (activeSchedule) {
        await api.put(`/schedules/${activeSchedule.schedule_id}`, { add_dates: [clickedDate], time_slot: activeSchedule.time_slot });
        // Update the new entry's time_slot if different from schedule default
        const updated = await api.get(`/schedules/${activeSchedule.schedule_id}`);
        const newEntry = updated.data.entries?.find(e => e.date === clickedDate);
        if (newEntry && timeSlot !== activeSchedule.time_slot) {
          const entries = updated.data.entries.map(e => e.entry_id === newEntry.entry_id ? { ...e, time_slot: timeSlot } : e);
          await api.put(`/schedules/${activeSchedule.schedule_id}`, { entries });
        }
      } else {
        await api.post('/schedules', { child_id: selectedChild.child_id, dates: [clickedDate], time_slot: timeSlot, schedule_type: 'session' });
      }
      toast.success(`Session added on ${new Date(clickedDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`);
      setClickedDate(null);
      setQuickAddTime({ startHour: '', startMinute: '00', startPeriod: 'AM', endHour: '', endMinute: '00', endPeriod: 'AM' });
      fetchSchedules();
    } catch { toast.error('Failed to add session'); }
    finally { setQuickAdding(false); }
  };

  const handleDrop = async (targetDate) => {
    if (!dragEntry || targetDate === dragEntry.date || targetDate < todayStr) {
      setDragEntry(null);
      setDragOverDate(null);
      return;
    }
    try {
      await api.put(`/schedules/${dragEntry.schedule_id}/entries/${dragEntry.entry_id}/reschedule`, {
        new_date: targetDate,
        new_time: dragEntry.time_slot || '',
      });
      const fromLabel = new Date(dragEntry.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const toLabel = new Date(targetDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      toast.success(`Session moved from ${fromLabel} to ${toLabel}`);
      fetchSchedules();
    } catch { toast.error('Failed to move session'); }
    finally { setDragEntry(null); setDragOverDate(null); }
  };


  // Calendar helpers
  const getDaysInMonth = (m, y) => new Date(y, m + 1, 0).getDate();
  const getFirstDayOfMonth = (m, y) => new Date(y, m, 1).getDay();
  const todayStr = new Date().toISOString().split('T')[0];

  const toggleDate = (dateStr) => {
    if (dateStr < todayStr) return;
    setSelectedDates(prev =>
      prev.includes(dateStr) ? prev.filter(d => d !== dateStr) : [...prev, dateStr].sort()
    );
  };

  const toggleRecurringDay = (dayIdx) => {
    setRecurringDays(prev =>
      prev.includes(dayIdx) ? prev.filter(d => d !== dayIdx) : [...prev, dayIdx].sort()
    );
  };

  // Aggregate all entries across schedules for calendar display
  const allEntries = useMemo(() => {
    const map = {};
    schedules.forEach(s => {
      (s.entries || []).forEach(e => {
        if (!map[e.date]) map[e.date] = [];
        map[e.date].push({ ...e, schedule_id: s.schedule_id, child_name: s.child_name, schedule_type: s.schedule_type });
      });
    });
    return map;
  }, [schedules]);

  // Build calendar grid
  const calendarGrid = useMemo(() => {
    const days = getDaysInMonth(calMonth, calYear);
    const firstDay = getFirstDayOfMonth(calMonth, calYear);
    const grid = [];
    for (let i = 0; i < firstDay; i++) grid.push(null);
    for (let d = 1; d <= days; d++) {
      const dateStr = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      grid.push(dateStr);
    }
    return grid;
  }, [calMonth, calYear]);

  if (loading) return <div className="flex items-center justify-center p-12"><Waves className="w-10 h-10 text-[#22D3EE] animate-pulse" /></div>;

  // Child selector if multiple children
  if (!selectedChild) {
    return (
      <div className="max-w-3xl mx-auto animate-fadeIn" data-testid="schedule-child-select">
        <h1 className="text-3xl font-extrabold text-slate-800 mb-6" style={{ fontFamily: 'Nunito' }}>Schedule</h1>
        <div className="grid gap-3">
          {children.map(c => (
            <Card key={c.child_id} className="rounded-2xl cursor-pointer hover:shadow-md transition-shadow" onClick={() => setSelectedChild(c)} data-testid={`select-child-${c.child_id}`}>
              <CardContent className="p-5 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#1E1B4B] flex items-center justify-center text-white font-bold text-lg" style={{ fontFamily: 'Nunito' }}>{c.name?.charAt(0)}</div>
                <span className="font-bold text-slate-800" style={{ fontFamily: 'Nunito' }}>{c.name}</span>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const monthLabel = new Date(calYear, calMonth).toLocaleString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="max-w-4xl mx-auto animate-fadeIn" data-testid="schedule-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-800" style={{ fontFamily: 'Nunito' }}>
            Schedule — {selectedChild.name}
          </h1>
          <p className="text-slate-500 mt-1">
            {isInstructor ? 'Create and manage session schedules' : 'View sessions and book meetings'}
          </p>
        </div>
        {isInstructor && (
          <Button data-testid="create-schedule-btn" onClick={() => setShowCreate(true)} className="rounded-full bg-[#1E1B4B] hover:bg-[#2D2B66] text-white font-bold gap-2">
            <Plus className="w-4 h-4" /> New Schedule
          </Button>
        )}
        {!isInstructor && (
          <Button data-testid="book-meeting-btn" onClick={() => { setShowBookMeeting(true); setMeetingDate(''); setMeetingTime(''); setMeetingType('meet_and_greet'); }} className="rounded-full bg-[#1E1B4B] hover:bg-[#2D2B66] text-white font-bold gap-2">
            <Handshake className="w-4 h-4" /> Book Meeting
          </Button>
        )}
      </div>

      {/* Calendar View */}
      <Card className="rounded-2xl border-slate-100 shadow-sm mb-6" data-testid="schedule-calendar">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <Button variant="ghost" size="icon" className="rounded-full" onClick={() => { if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); } else setCalMonth(m => m - 1); }}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <h2 className="font-bold text-slate-800 text-lg" style={{ fontFamily: 'Nunito' }}>{monthLabel}</h2>
            <Button variant="ghost" size="icon" className="rounded-full" onClick={() => { if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); } else setCalMonth(m => m + 1); }}>
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
          <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
            {DAY_NAMES.map(d => (
              <div key={d} className="text-center text-xs font-bold text-slate-400 py-2">{d}</div>
            ))}
            {calendarGrid.map((dateStr, i) => {
              if (!dateStr) return <div key={`empty-${i}`} />;
              const day = parseInt(dateStr.split('-')[2]);
              const entries = allEntries[dateStr] || [];
              const isToday = dateStr === todayStr;
              const isPast = dateStr < todayStr;
              const hasScheduled = entries.some(e => e.status === 'scheduled');
              const hasCompleted = entries.some(e => e.status === 'completed');
              const hasCancelled = entries.some(e => e.status === 'cancelled');
              const hasRescheduled = entries.some(e => e.status === 'rescheduled');
              const hasMeetAndGreet = entries.some(e => e.schedule_type === 'meet_and_greet' && e.status === 'scheduled');
              const isClickable = isInstructor || entries.length > 0;

              let dotColor = '';
              if (hasMeetAndGreet) dotColor = 'bg-lime-400';
              else if (hasScheduled) dotColor = 'bg-cyan-400';
              if (hasCompleted) dotColor = 'bg-green-400';
              if (hasCancelled) dotColor = 'bg-red-400';
              if (hasRescheduled) dotColor = 'bg-amber-400';

              return (
                <button
                  key={dateStr}
                  type="button"
                  onClick={() => isClickable && setClickedDate(dateStr)}
                  onDragOver={isInstructor && !isPast ? (e) => { e.preventDefault(); setDragOverDate(dateStr); } : undefined}
                  onDragLeave={isInstructor ? () => setDragOverDate(null) : undefined}
                  onDrop={isInstructor && !isPast ? (e) => { e.preventDefault(); handleDrop(dateStr); } : undefined}
                  className={`relative text-center py-3 sm:py-2 rounded-xl text-sm font-medium transition-all min-h-[44px]
                    ${isToday ? 'bg-[#1E1B4B] text-white font-bold' : isPast ? 'text-slate-300' : 'text-slate-700'}
                    ${isClickable && !isPast ? 'cursor-pointer hover:bg-cyan-50 hover:ring-2 hover:ring-cyan-200' : isPast && entries.length > 0 ? 'cursor-pointer hover:bg-slate-50' : 'cursor-default'}
                    ${clickedDate === dateStr ? 'ring-2 ring-cyan-400 bg-cyan-50' : ''}
                    ${dragOverDate === dateStr ? 'ring-2 ring-[#C4F017] bg-lime-50 scale-105' : ''}
                  `}
                  data-testid={`cal-day-${dateStr}`}
                >
                  {day}
                  {entries.length > 1 && (
                    <span className="absolute top-0.5 right-1 text-[9px] font-bold text-cyan-600">{entries.length}</span>
                  )}
                  {/* Draggable dots for instructor's scheduled entries, static dots for others */}
                  {isInstructor && entries.some(e => e.status === 'scheduled') ? (
                    entries.filter(e => e.status === 'scheduled').map(e => (
                      <span
                        key={e.entry_id}
                        draggable
                        onDragStart={(ev) => { ev.stopPropagation(); setDragEntry(e); ev.dataTransfer.effectAllowed = 'move'; }}
                        onDragEnd={() => { setDragEntry(null); setDragOverDate(null); }}
                        className={`absolute bottom-0.5 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full cursor-grab active:cursor-grabbing ${e.schedule_type === 'meet_and_greet' ? 'bg-lime-400' : 'bg-cyan-400'}`}
                        title={`Drag to reschedule${e.time_slot ? ` (${e.time_slot})` : ''}`}
                      />
                    ))
                  ) : (
                    dotColor && <span className={`absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full ${dotColor}`} />
                  )}
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-3 mt-4 text-xs">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-cyan-400" /> Scheduled</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-lime-400" /> Meet & Greet</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-green-400" /> Completed</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-400" /> Cancelled</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-amber-400" /> Reschedule Pending</span>
          </div>
          {isInstructor && <p className="text-xs text-slate-400 mt-2">Tap any date to add or manage sessions. Drag dots to reschedule.</p>}
        </CardContent>
      </Card>

      {/* Pending Meeting Requests */}
      {pendingMeetings.filter(m => m.status === 'pending' || m.status === 'countered').length > 0 && (
        <div className="mb-6">
          <h2 className="font-bold text-slate-700 text-lg mb-3" style={{ fontFamily: 'Nunito' }}>
            Meeting Requests
          </h2>
          <div className="space-y-3">
            {pendingMeetings.filter(m => m.status === 'pending' || m.status === 'countered').map(mg => {
              const needsMyAction = mg.proposed_by !== user?.role;
              const typeLabels = { meet_and_greet: 'Meet & Greet', re_evaluation: 'Re-evaluation', other: 'Meeting' };
              const typeLabel = typeLabels[mg.meeting_type] || 'Meeting';
              return (
                <Card key={mg.meet_greet_id} className={`rounded-2xl ${needsMyAction ? 'border-amber-300 bg-amber-50/50' : 'border-slate-100'}`} data-testid={`meeting-request-${mg.meet_greet_id}`}>
                  <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex items-center gap-3 flex-1">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${needsMyAction ? 'bg-amber-100' : 'bg-slate-100'}`}>
                        <Handshake className={`w-5 h-5 ${needsMyAction ? 'text-amber-600' : 'text-slate-400'}`} />
                      </div>
                      <div>
                        <p className="font-bold text-slate-800 text-sm">{typeLabel}</p>
                        <p className="text-xs text-slate-500">
                          {new Date(mg.proposed_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                          {mg.proposed_time && ` at ${mg.proposed_time}`}
                          {' · proposed by '}
                          <span className="capitalize">{mg.proposed_by_name || mg.proposed_by}</span>
                        </p>
                      </div>
                    </div>
                    {needsMyAction && (
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => approveMeeting(mg.meet_greet_id)} className="rounded-full bg-green-500 hover:bg-green-600 text-white font-bold gap-1 text-xs" data-testid={`approve-mg-${mg.meet_greet_id}`}>
                          <CheckCircle2 className="w-3 h-3" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => { setShowReschedule({ type: 'meeting', mg }); setRescheduleDate(mg.proposed_date); }} className="rounded-full border-amber-300 text-amber-700 font-bold gap-1 text-xs" data-testid={`reschedule-mg-${mg.meet_greet_id}`}>
                          <RefreshCw className="w-3 h-3" /> Reschedule
                        </Button>
                      </div>
                    )}
                    {!needsMyAction && (
                      <Badge className="rounded-full bg-blue-50 text-blue-600 border-blue-200 text-[10px]">Waiting for response</Badge>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Upcoming entries */}
      <h2 className="font-bold text-slate-700 text-lg mb-3" style={{ fontFamily: 'Nunito' }}>
        Upcoming Sessions
      </h2>
      {schedules.length === 0 ? (
        <Card className="rounded-3xl border-dashed border-2 border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <CalendarDays className="w-16 h-16 text-slate-300 mb-4" />
            <p className="text-xl font-bold text-slate-400" style={{ fontFamily: 'Nunito' }}>No Schedules Yet</p>
            <p className="text-slate-400 mt-2 text-sm">{isInstructor ? 'Create a schedule to get started' : 'Your instructor will set up sessions'}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {schedules.map(schedule => {
            const upcomingEntries = schedule.entries.filter(e => e.date >= todayStr && e.status !== 'cancelled');
            const pastEntries = schedule.entries.filter(e => e.date < todayStr);
            return (
              <Card key={schedule.schedule_id} className="rounded-2xl border-slate-100 shadow-sm" data-testid={`schedule-${schedule.schedule_id}`}>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      {schedule.schedule_type === 'meet_and_greet' ? <Handshake className="w-5 h-5 text-green-600" /> : <CalendarDays className="w-5 h-5 text-[#22D3EE]" />}
                      <span className="font-bold text-slate-800" style={{ fontFamily: 'Nunito' }}>{schedule.child_name}</span>
                      {schedule.schedule_type === 'meet_and_greet' && <Badge className="rounded-full bg-green-100 text-green-700 border-green-300 text-xs">Meet & Greet</Badge>}
                      {schedule.time_slot && <Badge variant="outline" className="rounded-full text-xs"><Clock className="w-3 h-3 mr-1" />{schedule.time_slot}</Badge>}
                    </div>
                    {isInstructor && (
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-600 rounded-full" onClick={() => deleteSchedule(schedule.schedule_id)} data-testid={`delete-schedule-${schedule.schedule_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  {schedule.notes && <p className="text-sm text-slate-500 mb-3">{schedule.notes}</p>}
                  <div className="space-y-2">
                    {upcomingEntries.map(entry => (
                      <div key={entry.entry_id} className={`flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-xl border gap-3 ${STATUS_STYLES[entry.status] || 'bg-slate-50 border-slate-200'}`} data-testid={`entry-${entry.entry_id}`}>
                        <div>
                          <span className="font-semibold text-sm">
                            {new Date(entry.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                          </span>
                          {entry.time_slot && <span className="text-xs ml-2 opacity-70">{entry.time_slot}</span>}
                          {entry.status === 'cancelled' && entry.cancel_reason && (
                            <p className="text-xs mt-1 opacity-70">Reason: {entry.cancel_reason}</p>
                          )}
                          {entry.status === 'rescheduled' && entry.reschedule_request && (
                            <p className="text-xs mt-1">
                              Requested: {new Date(entry.reschedule_request.proposed_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                              {entry.reschedule_request.reason && <span className="opacity-70"> — {entry.reschedule_request.reason}</span>}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="outline" className={`rounded-full text-xs capitalize w-fit ${STATUS_STYLES[entry.status]}`}>
                            {entry.status}
                          </Badge>
                          {/* Add to Calendar */}
                          {entry.status === 'scheduled' && (
                            <AddToCalendarButton
                              title={`${schedule.child_name} — ${schedule.schedule_type === 'meet_and_greet' ? 'Meet & Greet' : schedule.schedule_type === 're_evaluation' ? 'Re-evaluation' : `Swim Session`}`}
                              date={entry.date}
                              timeSlot={entry.time_slot || schedule.time_slot}
                              description={`SplashAble ${schedule.schedule_type === 'meet_and_greet' ? 'Meet & Greet' : schedule.schedule_type === 're_evaluation' ? 'Re-evaluation' : 'swim session'} with ${schedule.child_name}`}
                            />
                          )}
                          {/* Instructor: respond to reschedule */}
                          {isInstructor && entry.status === 'rescheduled' && entry.reschedule_request && !entry.reschedule_request.response && (
                            <div className="flex gap-2 ml-0 sm:ml-2">
                              <Button size="icon" className="h-9 w-9 rounded-full bg-green-500 hover:bg-green-600" onClick={() => handleRespondReschedule(schedule.schedule_id, entry.entry_id, true)} data-testid={`accept-reschedule-${entry.entry_id}`}>
                                <Check className="w-4 h-4 text-white" />
                              </Button>
                              <Button size="icon" className="h-9 w-9 rounded-full bg-red-500 hover:bg-red-600" onClick={() => handleRespondReschedule(schedule.schedule_id, entry.entry_id, false)} data-testid={`decline-reschedule-${entry.entry_id}`}>
                                <X className="w-4 h-4 text-white" />
                              </Button>
                            </div>
                          )}
                          {/* Parent/both: confirm, cancel or reschedule */}
                          {entry.status === 'scheduled' && (
                            <div className="flex gap-2 ml-0 sm:ml-2">
                              {!isInstructor && !entry.confirmed && (
                                <Button variant="ghost" size="icon" className="h-9 w-9 text-green-600 hover:bg-green-50 rounded-full" onClick={async () => { try { await api.put(`/schedules/${schedule.schedule_id}/entries/${entry.entry_id}/confirm`); toast.success('Attendance confirmed!'); fetchSchedules(); } catch { toast.error('Failed to confirm'); } }} data-testid={`confirm-btn-${entry.entry_id}`} title="Confirm Attendance">
                                  <Check className="w-4 h-4" />
                                </Button>
                              )}
                              {entry.confirmed && (
                                <Badge className="rounded-full bg-green-100 text-green-700 text-[10px] border-green-300">Confirmed</Badge>
                              )}
                              <Button variant="ghost" size="icon" className="h-9 w-9 text-amber-600 hover:bg-amber-50 rounded-full" onClick={() => setShowReschedule({ schedule_id: schedule.schedule_id, entry_id: entry.entry_id, date: entry.date })} data-testid={`reschedule-btn-${entry.entry_id}`} title="Request Reschedule">
                                <RefreshCw className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-9 w-9 text-red-500 hover:bg-red-50 rounded-full" onClick={() => setShowCancel({ schedule_id: schedule.schedule_id, entry_id: entry.entry_id, date: entry.date })} data-testid={`cancel-btn-${entry.entry_id}`} title="Cancel Session">
                                <Ban className="w-4 h-4" />
                              </Button>
                              {isInstructor && (
                                <Button variant="ghost" size="icon" className="h-9 w-9 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full" onClick={() => deleteEntry(schedule.schedule_id, entry.entry_id)} data-testid={`delete-entry-list-${entry.entry_id}`} title="Delete Session">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    {pastEntries.length > 0 && (
                      <details className="mt-2">
                        <summary className="text-xs text-slate-400 cursor-pointer hover:text-slate-600">Past sessions ({pastEntries.length})</summary>
                        <div className="space-y-2 mt-2">
                          {pastEntries.map(entry => (
                            <div key={entry.entry_id} className={`flex items-center justify-between p-3 rounded-xl border opacity-60 ${STATUS_STYLES[entry.status] || 'bg-slate-50 border-slate-200'}`}>
                              <span className="text-sm">
                                {new Date(entry.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                {entry.time_slot && <span className="text-slate-400 ml-2">{entry.time_slot}</span>}
                              </span>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className={`rounded-full text-xs capitalize ${STATUS_STYLES[entry.status]}`}>{entry.status}</Badge>
                                {isInstructor && entry.status !== 'completed' && (
                                  <Button variant="ghost" size="icon" className="h-6 w-6 text-red-400 hover:text-red-600 rounded-full" onClick={() => deleteEntry(schedule.schedule_id, entry.entry_id)} data-testid={`delete-past-entry-${entry.entry_id}`}><Trash2 className="w-3 h-3" /></Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Schedule Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Nunito' }} className="flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-[#22D3EE]" /> New Schedule — {selectedChild.name}
            </DialogTitle>
            <DialogDescription className="sr-only">Create a new session schedule</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 pt-2">
            {/* Session Type */}
            <div className="flex gap-2" data-testid="schedule-type-tabs">
              <button
                data-testid="type-session"
                onClick={() => setScheduleType('session')}
                className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 ${scheduleType === 'session' ? 'bg-[#22D3EE] text-white' : 'bg-slate-100 text-slate-500'}`}
              >
                <Waves className="w-4 h-4" /> Swim Session
              </button>
              <button
                data-testid="type-meet-and-greet"
                onClick={() => setScheduleType('meet_and_greet')}
                className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 ${scheduleType === 'meet_and_greet' ? 'bg-[#C4F017] text-[#1E1B4B]' : 'bg-slate-100 text-slate-500'}`}
              >
                <Handshake className="w-4 h-4" /> Meet & Greet
              </button>
            </div>

            {scheduleType === 'meet_and_greet' && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-3">
                <p className="text-xs text-green-700">
                  A Meet & Greet is an intro session to meet the parent & child, discuss the program, and co-create skill criteria and a session schedule together.
                </p>
              </div>
            )}

            {/* Mode selector */}
            <div className="flex gap-2" data-testid="schedule-mode-tabs">
              <button
                data-testid="mode-dates"
                onClick={() => setCreateMode('dates')}
                className={`flex-1 py-2.5 rounded-full text-sm font-bold transition-all ${createMode === 'dates' ? 'bg-[#1E1B4B] text-white' : 'bg-slate-100 text-slate-500'}`}
              >
                Pick Dates
              </button>
              <button
                data-testid="mode-recurring"
                onClick={() => setCreateMode('recurring')}
                className={`flex-1 py-2.5 rounded-full text-sm font-bold transition-all ${createMode === 'recurring' ? 'bg-[#1E1B4B] text-white' : 'bg-slate-100 text-slate-500'}`}
              >
                Recurring
              </button>
            </div>

            {createMode === 'dates' ? (
              <>
                {/* Calendar for picking dates */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Button variant="ghost" size="icon" className="rounded-full h-8 w-8" onClick={() => { if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); } else setCalMonth(m => m - 1); }}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="font-bold text-sm text-slate-700">{monthLabel}</span>
                    <Button variant="ghost" size="icon" className="rounded-full h-8 w-8" onClick={() => { if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); } else setCalMonth(m => m + 1); }}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                    {DAY_NAMES.map(d => <div key={d} className="text-center text-xs font-bold text-slate-400 py-1">{d}</div>)}
                    {calendarGrid.map((dateStr, i) => {
                      if (!dateStr) return <div key={`e-${i}`} />;
                      const day = parseInt(dateStr.split('-')[2]);
                      const isPast = dateStr < todayStr;
                      const isSelected = selectedDates.includes(dateStr);
                      return (
                        <button
                          key={dateStr}
                          disabled={isPast}
                          onClick={() => toggleDate(dateStr)}
                          className={`py-2 rounded-xl text-sm font-medium transition-all
                            ${isPast ? 'text-slate-200 cursor-not-allowed' : isSelected ? 'bg-[#22D3EE] text-white font-bold shadow-sm' : 'text-slate-700 hover:bg-[#ECFEFF]'}
                          `}
                          data-testid={`pick-date-${dateStr}`}
                        >
                          {day}
                        </button>
                      );
                    })}
                  </div>
                </div>
                {selectedDates.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {selectedDates.map(d => (
                      <Badge key={d} variant="outline" className="rounded-full bg-[#ECFEFF] text-[#0E7490] border-[#22D3EE]/30 gap-1">
                        {new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        <button onClick={() => toggleDate(d)} className="hover:text-red-500"><X className="w-3 h-3" /></button>
                      </Badge>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Recurring day picker */}
                <div>
                  <p className="text-sm font-semibold text-slate-600 mb-2">Repeat every:</p>
                  <div className="grid grid-cols-7 gap-2">
                    {DAY_FULL.map((name, idx) => (
                      <button
                        key={name}
                        onClick={() => toggleRecurringDay(idx)}
                        className={`py-3 rounded-xl text-xs font-bold transition-all ${recurringDays.includes(idx) ? 'bg-[#22D3EE] text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                        data-testid={`recurring-day-${idx}`}
                      >
                        {DAY_NAMES[idx]}
                      </button>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <button onClick={() => setRecurringDays([1, 2, 3, 4, 5])} className="text-xs px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 font-medium text-slate-600">Weekdays</button>
                    <button onClick={() => setRecurringDays([0, 6])} className="text-xs px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 font-medium text-slate-600">Weekends</button>
                    <button onClick={() => setRecurringDays([1, 3])} className="text-xs px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 font-medium text-slate-600">Mon & Wed</button>
                    <button onClick={() => setRecurringDays([2, 4])} className="text-xs px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 font-medium text-slate-600">Tue & Thu</button>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-600">Until date</label>
                  <Input type="date" value={recurringUntil} onChange={e => setRecurringUntil(e.target.value)} className="rounded-xl mt-1" data-testid="recurring-until" />
                </div>
              </>
            )}

            <div>
              <label className="text-sm font-semibold text-slate-600">Time Slot <span className="text-red-400">*</span></label>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {/* Start Time */}
                <div className="flex items-center gap-1">
                  <Select value={startHour} onValueChange={setStartHour}>
                    <SelectTrigger className="w-[72px] rounded-xl" data-testid="start-hour">
                      <SelectValue placeholder="HH" />
                    </SelectTrigger>
                    <SelectContent>
                      {HOURS.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <span className="text-slate-400 font-bold">:</span>
                  <Select value={startMinute} onValueChange={setStartMinute}>
                    <SelectTrigger className="w-[72px] rounded-xl" data-testid="start-minute">
                      <SelectValue placeholder="MM" />
                    </SelectTrigger>
                    <SelectContent>
                      {MINUTES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={startPeriod} onValueChange={setStartPeriod}>
                    <SelectTrigger className="w-[72px] rounded-xl" data-testid="start-period">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AM">AM</SelectItem>
                      <SelectItem value="PM">PM</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <span className="text-slate-400 font-bold text-sm">to</span>

                {/* End Time */}
                <div className="flex items-center gap-1">
                  <Select value={endHour} onValueChange={setEndHour}>
                    <SelectTrigger className="w-[72px] rounded-xl" data-testid="end-hour">
                      <SelectValue placeholder="HH" />
                    </SelectTrigger>
                    <SelectContent>
                      {HOURS.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <span className="text-slate-400 font-bold">:</span>
                  <Select value={endMinute} onValueChange={setEndMinute}>
                    <SelectTrigger className="w-[72px] rounded-xl" data-testid="end-minute">
                      <SelectValue placeholder="MM" />
                    </SelectTrigger>
                    <SelectContent>
                      {MINUTES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={endPeriod} onValueChange={setEndPeriod}>
                    <SelectTrigger className="w-[72px] rounded-xl" data-testid="end-period">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AM">AM</SelectItem>
                      <SelectItem value="PM">PM</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-600">Notes (optional)</label>
              <Textarea placeholder="Any notes for the parent..." value={notes} onChange={e => setNotes(e.target.value)} className="rounded-xl mt-1" rows={2} data-testid="schedule-notes" />
            </div>
            <Button data-testid="create-schedule-submit" onClick={createSchedule} disabled={creating} className="w-full rounded-full bg-[#1E1B4B] hover:bg-[#2D2B66] text-white font-bold h-11 gap-2">
              <CalendarDays className="w-4 h-4" /> {creating ? 'Creating...' : 'Create Schedule'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Date Detail Dialog (click on calendar day) */}
      <Dialog open={!!clickedDate} onOpenChange={() => setClickedDate(null)}>
        <DialogContent className="rounded-3xl max-w-md max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Nunito' }} className="flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-[#22D3EE]" />
              {clickedDate && new Date(clickedDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
            </DialogTitle>
            <DialogDescription className="sr-only">View and manage sessions for this date</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {/* Existing sessions on this date */}
            {clickedDate && (allEntries[clickedDate] || []).length > 0 ? (
              <div className="space-y-2">
                <p className="text-sm font-semibold text-slate-600">Sessions on this day</p>
                {(allEntries[clickedDate] || []).map(entry => (
                  <div key={entry.entry_id} className={`flex items-center justify-between p-3 rounded-xl border ${STATUS_STYLES[entry.status] || 'bg-slate-50 border-slate-200'}`} data-testid={`date-entry-${entry.entry_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {entry.schedule_type === 'meet_and_greet' ? <Handshake className="w-4 h-4 text-green-600 shrink-0" /> : <Waves className="w-4 h-4 text-cyan-600 shrink-0" />}
                        <span className="font-semibold text-sm">{entry.child_name}</span>
                        <Badge variant="outline" className={`rounded-full text-[10px] capitalize ${STATUS_STYLES[entry.status]}`}>{entry.status}</Badge>
                      </div>
                      {(entry.time_slot || '') && (
                        <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><Clock className="w-3 h-3" />{entry.time_slot}</p>
                      )}
                    </div>
                    {isInstructor && entry.status !== 'completed' && (
                      <Button
                        variant="ghost" size="icon"
                        className="h-8 w-8 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full shrink-0"
                        onClick={() => deleteEntry(entry.schedule_id, entry.entry_id)}
                        data-testid={`delete-entry-${entry.entry_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4">
                <CalendarDays className="w-10 h-10 text-slate-200 mx-auto mb-2" />
                <p className="text-sm text-slate-400">No sessions on this day</p>
              </div>
            )}

            {/* Quick-add for instructors (future dates only) */}
            {isInstructor && clickedDate && clickedDate >= todayStr && (
              <div className="border-t border-slate-100 pt-4">
                <p className="text-sm font-semibold text-slate-600 mb-3 flex items-center gap-1"><Plus className="w-4 h-4" /> Add Session</p>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-500">Time Slot</label>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      <div className="flex items-center gap-1">
                        <Select value={quickAddTime.startHour} onValueChange={v => setQuickAddTime(t => ({...t, startHour: v}))}>
                          <SelectTrigger className="w-[64px] rounded-xl h-9 text-xs" data-testid="quick-start-hour"><SelectValue placeholder="HH" /></SelectTrigger>
                          <SelectContent>{HOURS.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                        </Select>
                        <span className="text-slate-400 font-bold text-xs">:</span>
                        <Select value={quickAddTime.startMinute} onValueChange={v => setQuickAddTime(t => ({...t, startMinute: v}))}>
                          <SelectTrigger className="w-[64px] rounded-xl h-9 text-xs" data-testid="quick-start-min"><SelectValue /></SelectTrigger>
                          <SelectContent>{MINUTES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                        </Select>
                        <Select value={quickAddTime.startPeriod} onValueChange={v => setQuickAddTime(t => ({...t, startPeriod: v}))}>
                          <SelectTrigger className="w-[60px] rounded-xl h-9 text-xs" data-testid="quick-start-ampm"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="AM">AM</SelectItem><SelectItem value="PM">PM</SelectItem></SelectContent>
                        </Select>
                      </div>
                      <span className="text-slate-400 font-bold text-xs">to</span>
                      <div className="flex items-center gap-1">
                        <Select value={quickAddTime.endHour} onValueChange={v => setQuickAddTime(t => ({...t, endHour: v}))}>
                          <SelectTrigger className="w-[64px] rounded-xl h-9 text-xs" data-testid="quick-end-hour"><SelectValue placeholder="HH" /></SelectTrigger>
                          <SelectContent>{HOURS.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                        </Select>
                        <span className="text-slate-400 font-bold text-xs">:</span>
                        <Select value={quickAddTime.endMinute} onValueChange={v => setQuickAddTime(t => ({...t, endMinute: v}))}>
                          <SelectTrigger className="w-[64px] rounded-xl h-9 text-xs" data-testid="quick-end-min"><SelectValue /></SelectTrigger>
                          <SelectContent>{MINUTES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                        </Select>
                        <Select value={quickAddTime.endPeriod} onValueChange={v => setQuickAddTime(t => ({...t, endPeriod: v}))}>
                          <SelectTrigger className="w-[60px] rounded-xl h-9 text-xs" data-testid="quick-end-ampm"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="AM">AM</SelectItem><SelectItem value="PM">PM</SelectItem></SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  <Button
                    data-testid="quick-add-submit"
                    onClick={quickAddSession}
                    disabled={quickAdding}
                    className="w-full rounded-full bg-[#22D3EE] hover:bg-[#06B6D4] text-white font-bold h-10 gap-2"
                  >
                    <Plus className="w-4 h-4" /> {quickAdding ? 'Adding...' : 'Add Session'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={!!showCancel} onOpenChange={() => setShowCancel(null)}>
        <DialogContent className="rounded-3xl max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Nunito' }} className="flex items-center gap-2">
              <Ban className="w-5 h-5 text-red-500" /> Cancel Session
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {showCancel && (
              <p className="text-sm text-slate-600">
                Cancel the session on <strong>{new Date(showCancel.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</strong>?
              </p>
            )}
            <div>
              <label className="text-sm font-semibold text-slate-600">Reason (optional)</label>
              <Textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)} className="rounded-xl mt-1" placeholder="Let the instructor know why..." rows={2} data-testid="cancel-reason" />
            </div>
            <Button data-testid="confirm-cancel" onClick={handleCancelEntry} className="w-full rounded-full bg-red-500 hover:bg-red-600 text-white font-bold h-11 gap-2">
              <Ban className="w-4 h-4" /> Confirm Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reschedule Dialog */}
      <Dialog open={!!showReschedule} onOpenChange={() => setShowReschedule(null)}>
        <DialogContent className="rounded-3xl max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Nunito' }} className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-amber-500" /> Request Reschedule
            </DialogTitle>
            <DialogDescription className="sr-only">Request to reschedule a session</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {showReschedule && (
              <p className="text-sm text-slate-600">
                Reschedule from <strong>{new Date(showReschedule.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</strong>
              </p>
            )}
            <div>
              <label className="text-sm font-semibold text-slate-600">Proposed new date</label>
              <Input type="date" value={rescheduleDate} onChange={e => setRescheduleDate(e.target.value)} className="rounded-xl mt-1" data-testid="reschedule-date" />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-600">Proposed time</label>
              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                <div className="flex items-center gap-1">
                  <Select value={rescheduleTime.startHour} onValueChange={v => setRescheduleTime(t => ({...t, startHour: v}))}>
                    <SelectTrigger className="w-[64px] rounded-xl h-9 text-xs" data-testid="resched-start-hour"><SelectValue placeholder="HH" /></SelectTrigger>
                    <SelectContent>{HOURS.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                  </Select>
                  <span className="text-slate-400 font-bold text-xs">:</span>
                  <Select value={rescheduleTime.startMinute} onValueChange={v => setRescheduleTime(t => ({...t, startMinute: v}))}>
                    <SelectTrigger className="w-[64px] rounded-xl h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{MINUTES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={rescheduleTime.startPeriod} onValueChange={v => setRescheduleTime(t => ({...t, startPeriod: v}))}>
                    <SelectTrigger className="w-[60px] rounded-xl h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="AM">AM</SelectItem><SelectItem value="PM">PM</SelectItem></SelectContent>
                  </Select>
                </div>
                <span className="text-slate-400 font-bold text-xs">to</span>
                <div className="flex items-center gap-1">
                  <Select value={rescheduleTime.endHour} onValueChange={v => setRescheduleTime(t => ({...t, endHour: v}))}>
                    <SelectTrigger className="w-[64px] rounded-xl h-9 text-xs" data-testid="resched-end-hour"><SelectValue placeholder="HH" /></SelectTrigger>
                    <SelectContent>{HOURS.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                  </Select>
                  <span className="text-slate-400 font-bold text-xs">:</span>
                  <Select value={rescheduleTime.endMinute} onValueChange={v => setRescheduleTime(t => ({...t, endMinute: v}))}>
                    <SelectTrigger className="w-[64px] rounded-xl h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{MINUTES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={rescheduleTime.endPeriod} onValueChange={v => setRescheduleTime(t => ({...t, endPeriod: v}))}>
                    <SelectTrigger className="w-[60px] rounded-xl h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="AM">AM</SelectItem><SelectItem value="PM">PM</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-600">Reason (optional)</label>
              <Textarea value={rescheduleReason} onChange={e => setRescheduleReason(e.target.value)} className="rounded-xl mt-1" placeholder="Reason for reschedule..." rows={2} data-testid="reschedule-reason" />
            </div>
            <Button data-testid="confirm-reschedule" onClick={handleRescheduleEntry} className="w-full rounded-full bg-amber-500 hover:bg-amber-600 text-white font-bold h-11 gap-2">
              <RefreshCw className="w-4 h-4" /> Request Reschedule
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Book Meeting Dialog (Parent) */}
      <Dialog open={showBookMeeting} onOpenChange={setShowBookMeeting}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Nunito' }}>Book a Meeting</DialogTitle>
            <DialogDescription className="sr-only">Choose meeting type and propose a date</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-600">Meeting Type</label>
              {[
                { id: 'meet_and_greet', label: 'Meet & Greet', desc: 'Initial meeting before sessions begin' },
                { id: 're_evaluation', label: 'Re-evaluation', desc: 'Progress review at session milestones' },
                { id: 'other', label: 'Other', desc: 'General meeting or discussion' },
              ].map(t => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setMeetingType(t.id)}
                  className={`w-full flex items-center gap-3 rounded-2xl p-3 border-2 transition-all text-left ${
                    meetingType === t.id ? 'border-[#22D3EE] bg-[#ECFEFF]' : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                  data-testid={`book-type-${t.id}`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${meetingType === t.id ? 'bg-[#22D3EE]/20' : 'bg-slate-100'}`}>
                    <Handshake className={`w-4 h-4 ${meetingType === t.id ? 'text-[#0E7490]' : 'text-slate-500'}`} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-700">{t.label}</p>
                    <p className="text-[11px] text-slate-400">{t.desc}</p>
                  </div>
                </button>
              ))}
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-600">Preferred Date</label>
              <Input type="date" value={meetingDate} onChange={e => setMeetingDate(e.target.value)} className="rounded-xl mt-1" data-testid="book-meeting-date" min={new Date().toISOString().split('T')[0]} />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-600">Preferred Time (optional)</label>
              <Input type="time" value={meetingTime} onChange={e => setMeetingTime(e.target.value)} className="rounded-xl mt-1" data-testid="book-meeting-time" />
            </div>
            <Button onClick={bookMeeting} disabled={bookingMeeting || !meetingDate} className="w-full rounded-full bg-[#1E1B4B] hover:bg-[#2D2B66] text-white font-bold h-11 gap-2" data-testid="confirm-book-meeting">
              {bookingMeeting ? 'Sending...' : 'Send Meeting Request'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
